// Header.jsx
import React, { useState, useRef, useEffect } from "react";
import { FaBell, FaUserCircle, FaSearch, FaBars, FaChevronDown, FaBuilding, FaCheck } from "react-icons/fa";
import "./Header.css";
import { api, setAccessToken, clearCurrentWorkspace } from "../../services/api";
import { useAuthStore } from "../../services/useAuthStore";

// Stores que precisam ser limpos ao trocar de workspace
import useCrmStore from "../../services/useCrmStore";
import { useDashboardStore } from "../../services/useDashboardStore";

const Header = ({ toggleMobileMenu }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showWorkspaceSwitcher, setShowWorkspaceSwitcher] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const menuRef = useRef(null);
  const workspaceRef = useRef(null);

  // Dados globais do Zustand
  const user = useAuthStore((s) => s.user);
  const workspace = useAuthStore((s) => s.workspace);
  const workspaces = useAuthStore((s) => s.workspaces);
  const switchWorkspace = useAuthStore((s) => s.switchWorkspace);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  const handleToggleMenu = () => {
    setShowMenu((prev) => !prev);
    setShowWorkspaceSwitcher(false);
  };

  const handleToggleWorkspaceSwitcher = () => {
    setShowWorkspaceSwitcher((prev) => !prev);
    setShowMenu(false);
  };

  const handleClickOutside = (e) => {
    if (menuRef.current && !menuRef.current.contains(e.target)) {
      setShowMenu(false);
    }
    if (workspaceRef.current && !workspaceRef.current.contains(e.target)) {
      setShowWorkspaceSwitcher(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSwitchWorkspace = async (workspaceId) => {
    if (workspaceId === workspace?.id) {
      setShowWorkspaceSwitcher(false);
      return;
    }

    setIsSwitching(true);

    try {
      // Limpar stores antes de trocar
      useCrmStore.setState({
        stages: [],
        leads: [],
        leadsByStage: {},
        selectedLead: null,
        pagination: {},
      });

      useDashboardStore.setState({
        metrics: null,
        isLoading: false,
      });

      // Realizar a troca
      await switchWorkspace(workspaceId);
      setShowWorkspaceSwitcher(false);

      // Recarregar a pagina para garantir dados limpos
      window.location.reload();

    } catch (err) {
      console.error("Erro ao trocar workspace:", err);
    } finally {
      setIsSwitching(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post("/api/auth/logout");
      setAccessToken(null);
      clearCurrentWorkspace();
      clearAuth();
      window.location.replace("/login");
    } catch (err) {
      console.error("Erro ao fazer logout", err);
      // Mesmo com erro, limpar dados locais
      setAccessToken(null);
      clearCurrentWorkspace();
      clearAuth();
      window.location.replace("/login");
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      admin: "Admin",
      manager: "Gerente",
      operator: "Operador",
      finance: "Financeiro",
    };
    return roleLabels[role?.toLowerCase()] || role;
  };

  return (
    <header className="header">
      <div className="header-left">
        <h1>Dashboard</h1>
      </div>

      <div className="header-actions">
        {/* Workspace Switcher - so mostra se tem mais de 1 workspace */}
        {workspaces.length > 1 && (
          <div className="workspace-switcher-container" ref={workspaceRef}>
            <button
              className="workspace-switcher-button"
              onClick={handleToggleWorkspaceSwitcher}
              disabled={isSwitching}
            >
              <FaBuilding className="workspace-icon" />
              <span className="workspace-name">{workspace?.name || "Workspace"}</span>
              <FaChevronDown className={`workspace-chevron ${showWorkspaceSwitcher ? "open" : ""}`} />
            </button>

            {showWorkspaceSwitcher && (
              <div className="workspace-dropdown">
                <div className="workspace-dropdown-header">
                  <span>Trocar Workspace</span>
                </div>
                <div className="workspace-dropdown-list">
                  {workspaces.map((ws) => (
                    <button
                      key={ws.id}
                      className={`workspace-dropdown-item ${ws.id === workspace?.id ? "active" : ""}`}
                      onClick={() => handleSwitchWorkspace(ws.id)}
                      disabled={isSwitching}
                    >
                      <div className="workspace-dropdown-item-content">
                        <span className="workspace-dropdown-item-name">{ws.name}</span>
                        <span className="workspace-dropdown-item-role">{getRoleLabel(ws.role)}</span>
                      </div>
                      {ws.id === workspace?.id && (
                        <FaCheck className="workspace-dropdown-item-check" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Menu */}
        <div className="user-dropdown" ref={menuRef}>
          <button className="user-button" onClick={handleToggleMenu}>
            <FaUserCircle className="user-icon" />
          </button>

          {showMenu && (
            <div className="user-menu">
              <div className="user-menu-header">
                <span className="user-menu-name">{user?.name}</span>
                <span className="user-menu-email">{user?.email}</span>
              </div>
              <div className="user-menu-divider"></div>
              <button onClick={handleLogout} className="user-menu-logout">
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
